import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper,
  ListItemAvatar,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BackupIcon from '@mui/icons-material/Backup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NewAppForm from './NewAppForm';
import OverallAppBackup from './OverallAppBackup';
import { useMsal } from '@azure/msal-react';
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Grid,
  Button,
  Tooltip,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InfoIcon from '@mui/icons-material/Info';
import DeleteAppForm from './DeleteAppForm';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreAppForm from './RestoreAppForm';
import RestoreIcon from '@mui/icons-material/Restore';
import TimelineIcon from '@mui/icons-material/Timeline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AppLifecycleDetails from './AppLifecycleDetails';
import OwnershipAssignmentTool from './OwnershipAssignmentTool';
import { useAppCache } from '../context/AppCacheContext';

interface ActionsContentProps {
  searchQuery: string;
  onAppSelect?: (app: any) => void;
}

const ActionsContent: React.FC<ActionsContentProps> = ({ searchQuery, onAppSelect }) => {
  const { instance, accounts } = useMsal();
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<any>(null);
  const [tenantId, setTenantId] = useState<string | null>(window.localStorage.getItem('tenantId'));
  const { currentTenantId } = useAppCache();

  // History management refs (centralized like ToolsContent)
  const restoringFromHistory = useRef(false);
  const lastPushedView = useRef<string | null>(null);

  // View mode toggle state with persistence
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = localStorage.getItem('actionsViewMode');
    return saved === 'list' ? 'list' : 'cards';
  });

  useEffect(() => {
    localStorage.setItem('actionsViewMode', viewMode);
  }, [viewMode]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: 'cards' | 'list' | null
  ) => {
    if (next) setViewMode(next);
  };

  const actions = [
    {
      id: 'new-app',
      name: 'Register New Application',
      description: 'Create a new application registration in Azure AD',
      icon: <AddCircleOutlineIcon sx={{ color: '#4caf50', fontSize: '1.75rem' }} />
    },
    {
      id: 'delete-app',
      name: 'Delete Application',
      description: 'Permanently remove an application registration from Azure AD',
      icon: <DeleteForeverIcon sx={{ color: '#f44336', fontSize: '1.75rem' }} />
    },
    {
      id: 'restore-app',
      name: 'Restore Deleted Application',
      description: 'Recover recently deleted application registrations from Azure AD',
      icon: <RestoreIcon sx={{ color: '#ff9800', fontSize: '1.75rem' }} />
    },
    {
      id: 'backup',
      name: 'Backup Applications',
      description: 'Export all application registrations in your tenant',
      icon: <BackupIcon sx={{ color: '#2196f3', fontSize: '1.75rem' }} />
    },
    {
      id: 'lifecycle',
      name: 'View Application Lifecycle',
      description: 'Track creation, ownership, and change history for applications',
      icon: <TimelineIcon sx={{ color: '#7c3aed', fontSize: '1.75rem' }} />
    },
    {
      id: 'assign-owners',
      name: 'Assign App Owners',
      description: 'Bulk assign owners to applications lacking ownership',
      icon: <PersonAddIcon sx={{ color: '#9c27b0', fontSize: '1.75rem' }} />
    },
  ];

  const actionCategories = {
    "Application Lifecycle": ['new-app', 'delete-app', 'restore-app'],
    "Tenant Overview": ['backup', 'lifecycle', 'assign-owners']
  };

  const filteredActions = searchQuery
    ? actions.filter(a =>
        a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : actions;

  const getCategoryStyles = (category: string) => {
    const styles = {
      'Application Lifecycle': {
        color: '#0f172a',
        gradient: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        borderColor: '#334155',
        bgColor: '#e2e8f0',
        accent: '#059669'
      },
      'Tenant Overview': {
        color: '#312e81',
        gradient: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
        borderColor: '#4c1d95',
        bgColor: '#e0e7ff',
        accent: '#7c3aed'
      }
    };
    return styles[category as keyof typeof styles] || styles['Application Lifecycle'];
  };

  const getActionCardStyles = (actionId: string) => {
    const actionStyles = {
      'new-app': { iconBg: '#f0fdf4', iconColor: '#059669' },
      'delete-app': { iconBg: '#fef2f2', iconColor: '#dc2626' },
      'restore-app': { iconBg: '#fff7ed', iconColor: '#ea580c' },
      'backup': { iconBg: '#eff6ff', iconColor: '#2563eb' },
      'lifecycle': { iconBg: '#f3e8ff', iconColor: '#7c3aed' },
      'assign-owners': { iconBg: '#faf5ff', iconColor: '#9c27b0' }
    };
    return actionStyles[actionId as keyof typeof actionStyles] || { iconBg: '#f8fafc', iconColor: '#64748b' };
  };

  // ---- History logic ----
  const currentView = activeAction ? activeAction : 'actionsList';

  const pushViewState = (view: string) => {
    if (restoringFromHistory.current) return;
    if (lastPushedView.current === view) return;
    lastPushedView.current = view;
    window.history.pushState({ actionView: view }, '');
  };

  useEffect(() => {
    window.history.replaceState({ actionView: 'actionsList' }, '');
  }, []);

  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      restoringFromHistory.current = true;
      const s = (e.state || {}) as { actionView?: string };
      const view = s.actionView || 'actionsList';

      if (view === 'actionsList') {
        setActiveAction(null);
        setActionResult(null);
      } else {
        const valid = actions.some(a => a.id === view);
        if (valid) {
          setActiveAction(view);
        } else {
          setActiveAction(null);
        }
        setActionResult(null);
      }

      setTimeout(() => {
        restoringFromHistory.current = false;
        lastPushedView.current = view;
      }, 0);
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [actions]);

  useEffect(() => {
    if (currentView === 'actionsList') return;
    pushViewState(currentView);
  }, [currentView]);

  // ---- Original logic below (unchanged except Back handling) ----

  const handleActionSelect = (actionId: string) => {
    setActiveAction(actionId);
    setActionResult(null);
  };

  const handleBack = () => {
    if (window.history.length > 1 && currentView !== 'actionsList') {
      window.history.back();
      return;
    }
    setActiveAction(null);
    setActionResult(null);
    if (lastPushedView.current !== 'actionsList') {
      lastPushedView.current = 'actionsList';
      window.history.replaceState({ actionView: 'actionsList' }, '');
    }
  };

  const handleNewAppSubmit = async (appData: any) => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0]
      });

      const client = Client.init({
        authProvider: (done) => {
          done(null, response.accessToken);
        }
      });

      const result = await client.api('/applications').post(appData);
      const spData = { appId: result.appId };
      await client.api("/servicePrincipals").post(spData);

      setTimeout(() => {
        if (currentTenantId) {
          const timestampKey = `appconfig_applications_timestamp_${currentTenantId}`;
            localStorage.removeItem(timestampKey);
            console.log("🔄 Actions: Cache invalidated after app creation");
        }
      }, 2000);

      setActionResult({
        success: true,
        data: result,
        message: `Application "${result.displayName}" and its Service Principal created successfully!`
      });
    } catch (error) {
      console.error("Error creating application:", error);
      setActionResult({
        success: false,
        error,
        message: `Failed to create application: ${(error as any).message || "Unknown error"}`
      });
    }
  };

  const handleCacheRefresh = (delay: number = 2000) => {
    setTimeout(() => {
      if (currentTenantId) {
        const timestampKey = `appconfig_applications_timestamp_${currentTenantId}`;
        localStorage.removeItem(timestampKey);
        console.log("🔄 Actions: Cache invalidated after operation");
      }
    }, delay);
  };

  useEffect(() => {
    if (accounts.length > 0) {
      const currentAccount = accounts[0];
      if (currentAccount?.tenantId) {
        setTenantId(currentAccount.tenantId);
        window.localStorage.setItem('tenantId', currentAccount.tenantId);
      } else {
        instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: currentAccount
        }).then(response => {
          const claims = response.idTokenClaims as any;
          if (claims && claims.tid) {
            setTenantId(claims.tid);
            window.localStorage.setItem('tenantId', claims.tid);
          }
        }).catch(error => {
          console.warn("Could not get token to extract tenant ID:", error);
        });
      }
    }
  }, [accounts, instance]);

  const renderActiveAction = () => {
    switch (activeAction) {
      case 'new-app':
        return actionResult?.success ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: 'rgba(76, 175, 80, 0.1)',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <Box sx={{
                mr: 2,
                bgcolor: 'rgba(76, 175, 80, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.75rem' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                  {actionResult.data.displayName} is ready!
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Your new application and service principal have been created successfully
                </Typography>
              </Box>
            </Box>

            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Application Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Display Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {actionResult.data.displayName}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Application (Client) ID
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', mr: 1 }}>
                        {actionResult.data.appId}
                      </Typography>
                      <Tooltip title="Copy Application ID">
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(actionResult.data.appId)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Directory (Tenant) ID
                    </Typography>
                    {tenantId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', mr: 1 }}>
                          {tenantId}
                        </Typography>
                        <Tooltip title="Copy Tenant ID">
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(tenantId)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Loading tenant information...
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Object ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {actionResult.data.id}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'primary.main' }}>
                Next Steps
              </Typography>

              <Box sx={{
                mb: 3,
                p: 2,
                bgcolor: '#e3f2fd',
                borderRadius: 1,
                border: '1px solid #90caf9',
                display: 'flex',
                alignItems: 'flex-start'
              }}>
                <InfoIcon sx={{ color: '#1976d2', mr: 1.5, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#0d47a1', mb: 0.5 }}>
                    Configuration Actions Required
                  </Typography>
                  <Typography variant="body2">
                    Application "{actionResult.data.displayName}" has been created. To configure it,
                    go back to the main Applications list, then select this application to access its settings.
                    Below are the key configuration steps you'll need to complete.
                  </Typography>
                </Box>
              </Box>

              <List dense>
                <ListItem sx={{ px: 1, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckBoxOutlineBlankIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add redirect URIs for authentication flows"
                    secondary="Required for interactive user login in web or single-page applications"
                  />
                </ListItem>

                <ListItem sx={{ px: 1, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckBoxOutlineBlankIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Configure application permissions and scopes"
                    secondary="Set up API permissions and define custom scopes if your app exposes an API"
                  />
                </ListItem>

                <ListItem sx={{ px: 1, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckBoxOutlineBlankIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Create client secret or certificate"
                    secondary="Required for confidential client applications like web APIs"
                  />
                </ListItem>
              </List>
            </Paper>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  pl: 1.5,
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                    borderColor: "primary.main",
                    color: "primary.main"
                  }
                }}
              >
                Back to Actions
              </Button>
            </Box>
          </Box>
        ) : (
          <NewAppForm
            onCancel={handleBack}
            onSubmit={handleNewAppSubmit}
          />
        );

      case 'backup':
        return <OverallAppBackup onBack={handleBack} />;

      case 'delete-app':
        return (
          <DeleteAppForm
            onCancel={handleBack}
            onSuccess={() => handleCacheRefresh(1500)}
          />
        );

      case 'restore-app':
        return (
          <RestoreAppForm
            onCancel={handleBack}
            onSuccess={() => handleCacheRefresh(3000)}
          />
        );

      case 'lifecycle':
        return <AppLifecycleDetails onCancel={handleBack} />;

      case 'assign-owners':
        return (
          <OwnershipAssignmentTool
            onCancel={handleBack}
            onSuccess={() => handleCacheRefresh(2000)}
          />
        );

      default:
        return null;
    }
  };

  if (activeAction) {
    return renderActiveAction();
  }

  return (
    <Box sx={{ pb: 2 }}>
      {/* View mode toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          aria-label="view mode"
        >
          <ToggleButton value="cards" aria-label="card view">
            <ViewModuleIcon sx={{ fontSize: '1.2rem' }} />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon sx={{ fontSize: '1.2rem' }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {Object.entries(actionCategories).map(([category, actionIds], index) => {
        const categoryActions = filteredActions.filter(a => actionIds.includes(a.id));
        if (categoryActions.length === 0) return null;
        const categoryStyle = getCategoryStyles(category);
        const isLastCategory = index === Object.entries(actionCategories).length - 1;

        return (
          <div key={category}>
            <Box sx={{ mb: 5 }}>
              <Box
                sx={{
                  mb: 2.5,
                  px: 0,
                  py: 1,
                  borderRadius: 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.2rem',
                    color: categoryStyle.color,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  {category}
                  <Box
                    sx={{
                      flex: 1,
                      height: '1px',
                      backgroundColor: categoryStyle.borderColor,
                      opacity: 0.3,
                      ml: 2
                    }}
                  />
                  <Box
                    component="span"
                    sx={{
                      px: 2,
                      py: 0.5,
                      backgroundColor: categoryStyle.bgColor,
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: categoryStyle.color,
                      border: `1px solid ${categoryStyle.borderColor}20`,
                      opacity: 0.9,
                      textTransform: 'none',
                      letterSpacing: '0.25px'
                    }}
                  >
                    {category === 'Application Lifecycle' ? 'Direct App Operations' : 'Tenant-wide Tools'}
                  </Box>
                </Typography>
              </Box>

              {/* Cards View */}
              {viewMode === 'cards' && (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2.5,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    '@media (max-width: 768px)': {
                      gridTemplateColumns: '1fr'
                    }
                  }}
                >
                  {categoryActions.map(action => {
                    const cardStyle = getActionCardStyles(action.id);
                    return (
                    <Paper
                      key={action.id}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1.5px solid #e2e8f0',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                          borderColor: cardStyle.iconColor,
                          '& .action-icon': { transform: 'scale(1.1) rotate(5deg)' },
                          '& .action-card-bg': { opacity: 1, transform: 'scale(1.05)' }
                        }
                      }}
                      onClick={() => handleActionSelect(action.id)}
                    >
                      <Box
                        className="action-card-bg"
                        sx={{
                          position: 'absolute',
                          top: -20,
                          right: -20,
                          width: 100,
                          height: 100,
                          background: `linear-gradient(135deg, ${cardStyle.iconColor}15, ${cardStyle.iconColor}05)`,
                          borderRadius: '50%',
                          opacity: 0.7,
                          transform: 'scale(0.8)',
                          transition: 'all 0.3s ease',
                          zIndex: 0,
                        }}
                      />

                      <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              backgroundColor: cardStyle.iconBg,
                              border: `2px solid ${cardStyle.iconColor}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              transition: 'all 0.3s ease',
                              width: 56,
                              height: 56,
                            }}
                            className="action-icon"
                          >
                            {React.cloneElement(action.icon as React.ReactElement, {
                              sx: {
                                fontSize: '1.5rem',
                                color: cardStyle.iconColor,
                                filter: `drop-shadow(0 2px 4px ${cardStyle.iconColor}30)`,
                              }
                            })}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  color: '#1e293b',
                                  lineHeight: 1.2,
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {action.name}
                              </Typography>
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                color: '#64748b',
                                lineHeight: 1.5,
                                fontSize: '0.9rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {action.description}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between', // fix: was 'between'
                            alignItems: 'center',
                            pt: 2,
                            borderTop: '1px solid #f1f5f9',
                            width: '100%',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: cardStyle.iconColor,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {category === 'Application Lifecycle' ? 'lifecycle' : 'overview'}
                          </Typography>

                          <Box
                            sx={{
                              ml: 'auto', // ensure it sticks to the right
                              display: 'flex',
                              alignItems: 'center',
                              color: '#94a3b8',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                            }}
                          >
                            Click to start
                            <Box
                              component="span"
                              sx={{
                                ml: 1,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: 'currentColor',
                                opacity: 0.6,
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: 3,
                          border: `2px solid ${cardStyle.iconColor}`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          pointerEvents: 'none',
                          '.MuiPaper-root:hover &': {
                            opacity: 0.3,
                          }
                        }}
                      />
                    </Paper>
                  );
                })}
                </Box>
              )}

              {/* List View */}
              {viewMode === 'list' && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {categoryActions.map((action, idx) => {
                      const cardStyle = getActionCardStyles(action.id);
                      return (
                        <React.Fragment key={action.id}>
                          {idx > 0 && <Divider />}
                          <ListItem
                            component="div"
                            onClick={() => handleActionSelect(action.id)}
                            sx={{
                              py: 2,
                              px: 3,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(44, 62, 80, 0.04)',
                                '& .action-icon': {
                                  transform: 'scale(1.1)',
                                }
                              }
                            }}
                          >
                            <ListItemAvatar>
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  backgroundColor: cardStyle.iconBg,
                                  border: `2px solid ${cardStyle.iconColor}20`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease',
                                  width: 48,
                                  height: 48,
                                }}
                                className="action-icon"
                              >
                                {React.cloneElement(action.icon as React.ReactElement, {
                                  sx: {
                                    fontSize: '1.5rem',
                                    color: cardStyle.iconColor,
                                  }
                                })}
                              </Box>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#1e293b',
                                    mb: 0.5
                                  }}
                                >
                                  {action.name}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#64748b',
                                    lineHeight: 1.5
                                  }}
                                >
                                  {action.description}
                                </Typography>
                              }
                            />
                            <ArrowForwardIcon sx={{ color: cardStyle.iconColor, ml: 2 }} />
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>

            {!isLastCategory && (
              <Box sx={{ my: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                {[1, 2, 3, 4, 5].map(dot => (
                  <Box
                    key={dot}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#94a3b8',
                      opacity: dot === 3 ? 1 : 0.6
                    }}
                  />
                ))}
              </Box>
            )}
          </div>
        );
      })}

      {filteredActions.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <SearchIcon sx={{ fontSize: '2rem', color: '#94a3b8' }} />
          </Box>
          <Typography variant="h6" sx={{ mb: 1, color: '#64748b', fontWeight: 600 }}>
            No actions found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No actions match your search for "<strong>{searchQuery}</strong>"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search terms or browse all available actions.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ActionsContent;